import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ADMIN_UNAUTHORIZED_EVENT, verifyAdminToken } from '../../services/api';

/**
 * Listens for ADMIN_UNAUTHORIZED_EVENT emitted by the axios response
 * interceptor in services/api.ts (when any /api/v1/admin request returns 401),
 * and shows a dialog where the user can enter the admin token.
 *
 * On save, stores the token in localStorage.adminToken and reloads the page
 * so all in-flight queries re-run with the new header.
 */
const AdminLoginDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState('');
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onUnauthorized = () => {
      setToken(localStorage.getItem('adminToken') || '');
      setErr(null);
      setOpen(true);
    };
    window.addEventListener(ADMIN_UNAUTHORIZED_EVENT, onUnauthorized as EventListener);
    return () => {
      window.removeEventListener(ADMIN_UNAUTHORIZED_EVENT, onUnauthorized as EventListener);
    };
  }, []);

  // Verify the token against a real admin endpoint BEFORE storing it, so the
  // user gets a definite accepted/rejected instead of a blind store + reload
  // that just 401s again on the next request.
  const handleSave = async () => {
    const trimmed = (token || '').trim();
    if (!trimmed) return;
    setChecking(true);
    setErr(null);
    try {
      await verifyAdminToken(trimmed);
      localStorage.setItem('adminToken', trimmed);
      // Reload so any stale 401 data gets re-fetched with the accepted token.
      window.location.reload();
    } catch (e: any) {
      if (e?.response?.status === 401) {
        setErr('That token was rejected (401). Check for typos or extra spaces — it must match the server\'s ADMIN_API_TOKEN exactly.');
      } else {
        setErr(e?.message || 'Could not verify the token — try again.');
      }
      setChecking(false);
    }
  };

  const handleClear = () => {
    localStorage.removeItem('adminToken');
    setToken('');
    setErr(null);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Admin token required</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The admin API returned <code>401 Unauthorized</code>. Enter the admin token to continue;
          it is stored in <code>localStorage.adminToken</code> and sent as{' '}
          <code>x-admin-token</code> on all <code>/api/v1/admin/*</code> requests.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          type="password"
          label="Admin token"
          value={token}
          disabled={checking}
          onChange={(e) => setToken(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
          }}
        />
        {err && <Alert severity="error" sx={{ mt: 1.5 }}>{err}</Alert>}
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          The token is verified against the server before it's saved — if it's accepted, the page reloads.
        </Typography>
        <Alert severity="info" sx={{ mt: 1.5 }}>
          This only unlocks the admin API. It does <strong>not</strong> fix an expired
          Megaphone session — that banner clears only after you paste fresh MUM cookies and the
          live probe goes green.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear} color="inherit" disabled={checking}>[ CLEAR ]</Button>
        <Button onClick={() => setOpen(false)} disabled={checking}>[ CANCEL ]</Button>
        <Button
          onClick={handleSave}
          variant="outlined"
          color="warning"
          disabled={!token.trim() || checking}
          startIcon={checking ? <CircularProgress size={14} /> : undefined}
        >
          {checking ? 'Verifying…' : '[ VERIFY & SAVE ]'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminLoginDialog;
